import { useState } from "react";
import { Download } from "lucide-react";
import { nok, Card, Select, Btn, useData, Spinner, download } from "./ui";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Des"];
const cls = (v) => (v < 0 ? "text-red-400" : v > 0 ? "text-emerald-400" : "");

const Table = ({ title, sub, cols, rows, total, testid }) => (
  <Card className="overflow-x-auto" data-testid={testid}>
    <h4 className="font-display text-lg font-bold uppercase">{title}</h4>
    <p className="mb-3 text-xs text-zinc-400">{sub}</p>
    <table className="w-full min-w-[420px] text-right text-xs">
      <thead><tr className="border-b border-white/10 font-mono uppercase tracking-wider text-zinc-400"><th className="py-2 text-left">Mnd</th>{cols.map(([, l]) => <th key={l} className="py-2 pl-2">{l}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.month} className="border-b border-white/5 text-zinc-200">
            <td className="py-1.5 text-left">{MONTHS[r.month - 1]}</td>
            {cols.map(([k, , money, tone]) => <td key={k} className={`py-1.5 pl-2 ${tone ? cls(r[k]) : ""}`}>{money ? (r[k] ? nok(r[k]) : "–") : r[k] || "–"}</td>)}
          </tr>
        ))}
        <tr className="font-bold text-white"><td className="py-2 text-left">Året</td>
          {cols.map(([k, , money, tone]) => <td key={k} className={`py-2 pl-2 ${tone ? cls(total[k]) : ""}`} data-testid={`${testid}-total-${k}`}>{money ? nok(total[k]) : total[k]}</td>)}</tr>
      </tbody>
    </table>
  </Card>
);

export const SalesReport = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rep] = useData("/admin/reports/sales", { year });
  if (!rep || Array.isArray(rep)) return <Spinner />;
  const months = Array.isArray(rep.months) ? rep.months : [];
  const t = rep.total ?? {};
  const max = Math.max(1, ...months.map((m) => Math.abs(m.profit)));
  return (
    <section className="space-y-4" data-testid="sales-report">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h3 className="font-display text-2xl font-bold uppercase">Bilsalg – rapport</h3><p className="text-xs text-zinc-400">Beløp eks. utgående MVA. Kostnader eks. fradragsberettiget MVA.</p></div>
        <div className="flex flex-wrap gap-2">
          <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))} options={(rep.years ?? [year]).map((y) => [String(y), String(y)])} className="w-32" data-testid="report-year-select" />
          <Btn onClick={() => download(`/admin/reports/sales.pdf?year=${rep.year}`, `rbtun_salgsrapport_${rep.year}.pdf`)} data-testid="report-export-pdf"><Download size={14} /> PDF</Btn>
          <Btn onClick={() => download(`/admin/reports/sales.csv?year=${rep.year}`, `rbtun_salgsrapport_${rep.year}.csv`)} data-testid="report-export-csv"><Download size={14} /> CSV</Btn>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[["Solgte biler", t.sold, false], ["Salgsinntekt", t.revenue_net, true], ["Kostnad solgte biler", t.cost, true], ["Fortjeneste", t.profit, true]].map(([l, v, m]) => (
          <Card key={l} className="p-4"><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{l}</div>
            <div className={`mt-1 font-display text-2xl font-black ${l === "Fortjeneste" ? cls(v) : ""}`} data-testid={`report-${l}`}>{m ? nok(v) : v ?? 0}</div></Card>
        ))}
      </div>
      <Card data-testid="report-chart">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400">Fortjeneste per salgsmåned</p>
        <div className="flex h-32 items-end gap-1">
          {months.map((m) => (
            <div key={m.month} className="flex flex-1 flex-col items-center gap-1" title={`${MONTHS[m.month - 1]}: ${nok(m.profit)}`}>
              <div className={`w-full ${m.profit < 0 ? "bg-red-500/70" : "bg-[var(--red)]"}`} style={{ height: `${(Math.abs(m.profit) / max) * 100}px` }} />
              <span className="text-[10px] text-zinc-400">{MONTHS[m.month - 1].slice(0, 1)}</span>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-4 xl:grid-cols-2">
        <Table title="Etter salgsmåned" sub="Alle kostnader for en solgt bil regnes i måneden den ble solgt." testid="report-by-sale" rows={months} total={t}
          cols={[["sold", "Solgt"], ["revenue_net", "Inntekt", true], ["sale_vat", "MVA", true], ["cost", "Kostnad", true], ["profit", "Fortjeneste", true, true]]} />
        <Table title="Etter utgiftsdato" sub="Utgifter i måneden de ble betalt, også for biler som ikke er solgt ennå." testid="report-by-expense" rows={months} total={t}
          cols={[["purchases", "Innkjøp"], ["revenue_net", "Inntekt", true], ["expenses", "Utgifter", true], ["cash_result", "Resultat", true, true]]} />
      </div>
    </section>
  );
};
